import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { Group } from '@visx/group';
import { BarStack, BarRounded } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { localPoint } from '@visx/event';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { formatDate } from '../helpers';
import { useTranslation } from 'react-i18next';
import styles from './stackbar-graph.module.scss';
import { useSelector } from 'react-redux';
import { useSpring, animated } from 'react-spring';
import { DateTime } from 'luxon';
import { formatToMaxFourDigits, IconWrapper } from '@flexo/general';

const ENV = (import.meta as any).env;
const browserLanguage = navigator.language;

/**
 * Consolidated chart configuration object that replaces scattered magic numbers
 * throughout the stackbar component. This provides:
 * 
 * 1. Platform-specific adjustments (web vs mobile)
 * 2. Chart dimension calculations 
 * 3. Interaction settings
 * 4. Axis positioning adjustments
 * 
 * Benefits:
 * - All magic numbers are documented and centralized
 * - Consistent platform-specific behavior
 * - Easy to maintain and modify dimensions
 * - Clear separation of concerns
 */
const CHART_CONFIG = {
  // Platform-specific adjustments
  web: {
    defaultHeight: 350, // Default chart height for web
    leftMargin: 35, // Left margin for web (more space for axis labels)
    containerPadding: 0, // Padding applied to container width
    headerHeight: 48, // Height reserved for header/legend
  },
  mobile: {
    defaultHeight: 300, // Default chart height for mobile
    leftMargin: 30, // Left margin for mobile (less space)
    containerPadding: 16, // Same padding as web
    headerHeight: 48, // Same header height as web
  },
  // Chart dimensions
  chartPadding: {
    bottom: 16, // Bottom margin default
    top: 30, // Top margin default
    yAxisBuffer: 8, // Extra buffer for Y-axis calculations
    groupOffset: 2, // Offset for bar group positioning
  },
  // Interaction and display
  interaction: {
    minVibrationInterval: 50, // Minimum time between haptic feedback
  },
  // Axis positioning adjustments
  axis: {
    bottomTopOffset: 16, // Bottom axis top position adjustment
    leftTopOffset: -10, // Left axis top position adjustment
  },
};

interface GraphKeyConfig {
  key: string;
  color: string;
  combined: boolean;
  label?: string;
}

export interface StackbarVisxProps {
  _data: Array<any>;
  graphKeys: GraphKeyConfig[];
  title: string;
  unit: string;
  subtype: string;
  gradientBorder?: boolean;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  isLoading?: boolean;
  isLoadingError?: boolean;
  originalData?: any;
  totalValue?: any;
}

// Platform-specific default margin using configuration
const isWeb = ENV?.VITE_APP_IS_WEB === 'true';
const platformConfig = isWeb ? CHART_CONFIG.web : CHART_CONFIG.mobile;

const defaultMargin = { 
  top: CHART_CONFIG.chartPadding.top, 
  right: 0, 
  bottom: CHART_CONFIG.chartPadding.bottom, 
  left: platformConfig.leftMargin 
};

function getGreyScaledColor(color: string) {
  let _color = color;
  if (color?.includes('#')) {
    try {
      const red = parseInt(color.substr(1, 2), 16);
      const green = parseInt(color.substr(3, 2), 16);
      const blue = parseInt(color.substr(5, 2), 16);
      const grayscale = Math.round(0.299 * red + 0.587 * green + 0.114 * blue);
      const grayscaleHex = grayscale.toString(16).padStart(2, '0');
      _color = `#${grayscaleHex}${grayscaleHex}${grayscaleHex}60`;
    } catch {
      return color;
    }
  }
  return _color;
}

const AnimatedVisxBarRounded = animated(BarRounded);

interface AnimatedBarProps {
  xPos: number;
  initialY: number;
  initialHeight: number;
  targetY: number;
  targetHeight: number;
  width: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  radius: number;
  radiusTopLeft: boolean;
  radiusTopRight: boolean;
  radiusBottomLeft: boolean;
  radiusBottomRight: boolean;
  shouldAnimate: boolean;
  delay?: number;
  springConfig?: { tension?: number; friction?: number; mass?: number };
  isCombined?: boolean;
  barXForCombined?: number;
  barWidthForCombined?: number;
}

const AnimatedBar: React.FC<AnimatedBarProps> = ({
  xPos,
  initialY,
  initialHeight,
  targetY,
  targetHeight,
  width,
  fill,
  stroke,
  strokeWidth,
  radius,
  radiusTopLeft,
  radiusTopRight,
  radiusBottomLeft,
  radiusBottomRight,
  shouldAnimate,
  delay = 0,
  springConfig = { tension: 170, friction: 26 },
  isCombined,
  barXForCombined,
  barWidthForCombined,
}) => {
  const springProps = useSpring({
    from: { y: initialY, height: initialHeight },
    to: shouldAnimate
      ? { y: targetY, height: targetHeight, delay }
      : { y: initialY, height: initialHeight, immediate: true, delay: 0 },
    config: springConfig,
  });

  const actualXForBarRounded = xPos;

  if (isCombined) {
    const originalBarWidth = barWidthForCombined ?? 0;

    if (originalBarWidth === 1) {
      return (
        <AnimatedVisxBarRounded
          x={barXForCombined ?? 0}
          y={springProps.y}
          height={springProps.height}
          width={1}
          fill={fill}
          stroke={fill}
          strokeWidth={0}
          radius={radius}
          topLeft={radiusTopLeft}
          topRight={radiusTopRight}
          bottomLeft={radiusBottomLeft}
          bottomRight={radiusBottomRight}
        />
      );
    } else if (originalBarWidth < 1) {
      return null;
    }

    const drawSideLines = targetHeight > 0;

    return (
      <g>
        {drawSideLines && (
          <>
            <animated.rect
              x={(barXForCombined ?? 0) - 0.5}
              y={springProps.y}
              width={1}
              height={springProps.height}
              fill="white"
            />
            <animated.rect
              x={(barXForCombined ?? 0) + originalBarWidth - 0.5}
              y={springProps.y}
              width={1}
              height={springProps.height}
              fill="white"
            />
          </>
        )}
        <AnimatedVisxBarRounded
          x={actualXForBarRounded}
          y={springProps.y}
          height={springProps.height}
          width={width}
          fill={fill}
          stroke={fill}
          strokeWidth={0}
          radius={radius}
          topLeft={radiusTopLeft}
          topRight={radiusTopRight}
          bottomLeft={radiusBottomLeft}
          bottomRight={radiusBottomRight}
        />
      </g>
    );
  } else {
    return (
      <AnimatedVisxBarRounded
        x={actualXForBarRounded}
        y={springProps.y}
        height={springProps.height}
        width={width}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        radius={radius}
        topLeft={radiusTopLeft}
        topRight={radiusTopRight}
        bottomLeft={radiusBottomLeft}
        bottomRight={radiusBottomRight}
      />
    );
  }
};

export const Stackbar: React.FC<StackbarVisxProps> = ({
  _data = [],
  graphKeys = [],
  title,
  unit,
  subtype,
  height = isWeb ? CHART_CONFIG.web.defaultHeight : CHART_CONFIG.mobile.defaultHeight,
  margin = defaultMargin,
  isLoading = false,
  isLoadingError = false,
  originalData,
  totalValue,
}) => {
  const { t } = useTranslation();
  const ThemeStore = useSelector((state: any) => state.theme);
  const calendar = useSelector((state: any) => state.calendar);
  const { selectedTimeSet } = calendar.hiveCalendar;
  const chartRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [chartWidth, setChartWidth] = useState(() => {
    // Initialize with parent width if available, otherwise use a reasonable default
    return typeof window !== 'undefined' ? window.innerWidth - (platformConfig.containerPadding * 2) : 800;
  });

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>(() =>
    graphKeys.reduce((acc, gk) => {
      acc[gk.key] = true;
      return acc;
    }, {} as Record<string, boolean>)
  );

  const [animate, setAnimate] = useState(false);
  const prevIsLoadingRef = useRef(isLoading);
  const animationTimeoutRef = useRef<number | null>(null);

  const lastHoveredDateRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastVibrationTimeRef = useRef(0);

  // Replaced Haptics.selection() with Haptics.impact({ style: ImpactStyle.Light }) because TypeScript only exposed impact.
  // Added a 50 ms throttle (MIN_VIBRATION_INTERVAL) to avoid overloading the haptic engine during fast drags.
  // Wrapped pointer‐move logic in requestAnimationFrame so we only update selectedDate once per frame.
  // Only trigger a haptic when the bar's date under the finger actually changes (tracked by lastHoveredDateRef).
  // Kept selectedDate active until the finger truly leaves the SVG (handlePointerLeave), so "unselected" state no longer flickers.
  const triggerThrottledHaptic = () => {
    if (isWeb) return;
    const now = Date.now();
    if (now - lastVibrationTimeRef.current >= CHART_CONFIG.interaction.minVibrationInterval) {
      lastVibrationTimeRef.current = now;
      Haptics.impact({ style: ImpactStyle.Light }).catch((err) => {
        console.warn('Haptics.impact() error:', err);
      });
    }
  };

  const getDate = (d: any) => d.date;

  const processedData = useMemo(() => {
    const filteredKeys = graphKeys.filter((gk) => gk.combined);
    return (_data || []).map((d) => {
      const newEntry: any = { date: getDate(d) };
      graphKeys.forEach((gk) => {
        const complexKey = `widgets.${subtype}.${gk.key}`;
        let value = d[complexKey];
        if (!visibleKeys[gk.key] || value == null || value < 0) {
          value = 0;
        } else if (gk.combined && (gk as any).base) {
          value =
            d[complexKey] - d[`widgets.${subtype}.${filteredKeys[0].key}`];
        }
        newEntry[gk.key] = value;
      });
      return newEntry;
    });
  }, [_data, graphKeys, visibleKeys, subtype]);

  const seriesKeys = useMemo(() => graphKeys.map((gk) => gk.key), [graphKeys]);
  const seriesKeyConfigs = useMemo(() => {
    const map = new Map<string, GraphKeyConfig>();
    graphKeys.forEach((gk) => map.set(gk.key, gk));
    return map;
  }, [graphKeys]);


  // Calculate chart dimensions using configuration
  // xMax = available width - margins - container padding
  // yMax = available height - margins - header space - axis buffer
  const xMax = Math.max(0, chartWidth - margin.left - margin.right - platformConfig.containerPadding);
  const yMax = Math.max(0, height - margin.top - margin.bottom - platformConfig.headerHeight - CHART_CONFIG.chartPadding.yAxisBuffer);

  const dateScale = useMemo(
    () =>
      scaleBand<string>({
        range: [0, xMax],
        domain: processedData.map(getDate),
        padding: 0.2, //this is the vertical padding between the bars
      }),
    [xMax, processedData]
  );

  const yScale = useMemo(() => {
    const allPositiveStackValues = processedData.map((d) =>
      seriesKeys.reduce((sum, key) => sum + Math.max(0, d[key] || 0), 0)
    );
    const maxPositiveStackValue = Math.max(...allPositiveStackValues, 0);
    return scaleLinear<number>({
      range: [yMax, 0],
      domain: [0, maxPositiveStackValue > 0 ? maxPositiveStackValue : 1],
      nice: true,
    });
  }, [yMax, processedData, seriesKeys]);

  const colorScale = useMemo(
    () =>
      scaleOrdinal<string, string>({
        domain: seriesKeys,
        range: graphKeys.map((gk) => ThemeStore.colors[gk.color] || '#8884d8'),
      }),
    [seriesKeys, graphKeys, ThemeStore.colors]
  );

  // Check for null data based on original data, not processed data
  const isNullData = useMemo(() => {
    if (!isLoading && _data.length === 0) return true;
    if (
      !isLoading &&
      _data.every((d) =>
        seriesKeys.every((key) => {
          const complexKey = `widgets.${subtype}.${key}`;
          return d[complexKey] === null || d[complexKey] === undefined;
        })
      )
    )
      return true;
    return false;
  }, [isLoading, _data, seriesKeys, subtype]);

  // Check if all original data values are 0, regardless of visibility
  const isInherentlyZeroData = useMemo(() => {
    if (isNullData || isLoading) return false;
    if (_data.length === 0) return false;
    return _data.every((d) =>
      seriesKeys.every((key) => {
        const complexKey = `widgets.${subtype}.${key}`;
        const value = d[complexKey];
        return value === 0 || value === null || value === undefined;
      })
    );
  }, [isLoading, _data, seriesKeys, isNullData, subtype]);

  // Check if currently visible data appears to be 0 (could be due to disabled keys)
  const is0ValueData = useMemo(() => {
    if (isNullData || isLoading) return false;
    if (processedData.length === 0) return false;
    return processedData.every((d) =>
      seriesKeys.every(
        (key) => d[key] === 0 || d[key] === null || d[key] === undefined
      )
    );
  }, [isLoading, processedData, seriesKeys, isNullData]);

  const handlePointerLeave = () => {
    // Don't handle interactions if data is null or all zero
    if (isNullData || is0ValueData) return;

    // Cancel any pending animation frame
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Always reset state regardless of current state
    lastHoveredDateRef.current = null;
    setSelectedDate(null);
  };

  const handlePointerMove = (
    event: React.MouseEvent<SVGSVGElement> | React.TouchEvent<SVGSVGElement>
  ) => {
    // Don't handle interactions if data is null or all zero
    if (isNullData || is0ValueData) return;

    if (!svgRef.current) return;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = requestAnimationFrame(() => {
      const point = localPoint(svgRef.current!, event);
      if (!point) {
        return;
      }

      const { x } = point;
      const pointerX = x - margin.left;
      const bandwidth = dateScale.bandwidth();

      let newHoverDate: string | null = null;
      let closestDist = Infinity;

      processedData.forEach((d) => {
        const dateX = dateScale(getDate(d));
        if (dateX !== undefined) {
          const centerX = dateX + bandwidth / 2;
          const dist = Math.abs(pointerX - centerX);
          if (
            pointerX >= dateX &&
            pointerX <= dateX + bandwidth &&
            dist < closestDist
          ) {
            closestDist = dist;
            newHoverDate = getDate(d);
          }
        }
      });

      if (
        newHoverDate !== null &&
        newHoverDate !== lastHoveredDateRef.current
      ) {
        lastHoveredDateRef.current = newHoverDate;
        setSelectedDate(newHoverDate);
        if (event.type.startsWith('touch')) {
          triggerThrottledHaptic();
        }
      }
    });
  };

  // Enhanced cleanup function for touch interactions
  const cleanupTouchInteraction = () => {
    // Don't handle interactions if data is null or all zero
    if (isNullData || is0ValueData) return;

    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    lastHoveredDateRef.current = null;
    setSelectedDate(null);
  };

  const getGraphKeyLabel = (key: string) => {
    return t(`widgets.${subtype}.${key}`);
  };

  const selectedBarData = useMemo(() => {
    if (!selectedDate) return null;
    return originalData.find((d) => getDate(d) === selectedDate) || null;
  }, [selectedDate, _data, getDate, originalData]);

  const legendItems = useMemo(() => {
    const items: Array<{
      type: 'single' | 'group';
      id: string;
      label?: string;
      colorKey?: string;
      config?: GraphKeyConfig;
      memberConfigs?: GraphKeyConfig[];
    }> = [];
    const nonCombined: GraphKeyConfig[] = [];
    const combinedMembers: GraphKeyConfig[] = [];

    graphKeys.forEach((gk) => {
      if (gk.combined) {
        combinedMembers.push(gk);
      } else {
        nonCombined.push(gk);
      }
    });

    nonCombined.forEach((gk) => {
      items.push({
        type: 'single',
        id: gk.key,
        label: getGraphKeyLabel(gk.key),
        colorKey: gk.key,
        config: gk,
      });
    });

    if (combinedMembers.length > 0) {
      items.push({
        type: 'group',
        id: '__combined_group__',
        memberConfigs: combinedMembers,
      });
    }
    return items;
  }, [graphKeys, getGraphKeyLabel, t, subtype]);

  const toggleKeyVisibility = (keyToToggle: string) => {
    // Disable toggling only when there is no data (null) or all original values are inherently zero.
    if (isInherentlyZeroData || isNullData) return;
    setVisibleKeys((prev) => {
      const willBeVisible = !prev[keyToToggle];
      const currentlyVisibleKeys = seriesKeys.filter((k) => prev[k]).length;

      if (!willBeVisible && currentlyVisibleKeys === 1 && prev[keyToToggle]) {
        return prev;
      }

      return {
        ...prev,
        [keyToToggle]: willBeVisible,
      };
    });
  };

  // --- TEST OLD ANIMATION ---
  /*
  useEffect(() => {
    const justFinishedLoading = prevIsLoadingRef.current && !isLoading;
    prevIsLoadingRef.current = isLoading;

    if (animationTimeoutRef.current) {
      window.clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    if (isLoading) {
      if (animate) {
        setAnimate(false);
      }
    } else {
      if (_data.length > 0) {
        if (justFinishedLoading) {
          setAnimate(false);
          animationTimeoutRef.current = window.setTimeout(() => {
            setAnimate(true);
            animationTimeoutRef.current = null;
          }, 50);
        } else {
          if (!animate) {
            setAnimate(true);
          }
        }
      } else {
        if (animate) {
          setAnimate(false);
        }
      }
    }

    return () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current);
      }
      // Also cleanup any pending pointer animation frames
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [_data, isLoading, animate]);
  */

  // --- TEST FOR NEW ANIMATION ---
  useEffect(() => {
    // L'animazione parte solo al primo caricamento, poi i dati si aggiornano senza azzerarsi
    if (!isLoading && _data.length > 0 && !animate) {
      setAnimate(true);
    }
    // Se vuoi che l'animazione parta solo una volta, puoi anche togliere del tutto la gestione di animate
    // e passare sempre shouldAnimate={false} a AnimatedBar.
  }, [isLoading, _data]);

  // useLayoutEffect to measure before paint:
  useLayoutEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        const containerWidth =
          chartRef.current.offsetWidth ||
          chartRef.current.parentElement?.offsetWidth ||
          window.innerWidth - (platformConfig.containerPadding * 2);
        setChartWidth(Math.max(containerWidth, 200)); // Ensure minimum width
      }
    };

    updateWidth();
    // Use ResizeObserver for more reliable width updates
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (chartRef.current) {
      const containerWidth =
        chartRef.current.offsetWidth ||
        chartRef.current.parentElement?.offsetWidth ||
        window.innerWidth - (platformConfig.containerPadding * 2);
      setChartWidth(Math.max(containerWidth, 200));
    }
  }, [_data]);

  useEffect(() => {
    setVisibleKeys(
      graphKeys.reduce((acc, gk) => {
        acc[gk.key] = true;
        return acc;
      }, {} as Record<string, boolean>)
    );
  }, [graphKeys]);

  // Cleanup effect for component unmounting
  useEffect(() => {
    return () => {
      // Ensure all cleanup happens when component unmounts
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, []);

  // if (chartWidth > 0 && isLoading) {
  //   return (
  //     <div
  //       ref={chartRef}
  //       style={{
  //         display: 'flex',
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         width: '100%',
  //         height: height,
  //       }}
  //     >
  //       <ChartSkeleton height={height +48} title={title} />
  //     </div>
  //   );
  // }

  if (xMax <= 0 || (yMax <= 0 && chartWidth === 0)) {
    return <div ref={chartRef} style={{ width: '100%', height }} />;
  }

  const axisLineColor = ThemeStore.colors.ui_light || '#F7F7F7';
  const axisTickLabelColor = ThemeStore.colors.text_subdued || '#838B93';

  const generateTickValues = (xMin: number, xMax: number): number[] => {
    if (xMin === xMax) return [xMin];
    const numTicks = 4; // Always generate 4 tick values
    const step = (xMax - xMin) / (numTicks - 1);
    if (numTicks <= 1 || numTicks - 1 === 0) return [xMin];
    return Array.from({ length: numTicks }, (_, i) => {
      const tickTime = xMin + i * step;
      const date = new Date(tickTime);
      date.setMinutes(0, 0, 0);
      return date.getTime();
    });
  };

  let finalTickValues: string[] | undefined = undefined;
  const dateScaleDomain = dateScale.domain();

  if (dateScaleDomain.length > 0) {
    const domainTimestamps = dateScaleDomain.map((d) => new Date(d).getTime());
    const actualXMin = Math.min(...domainTimestamps);
    const actualXMax = Math.max(...domainTimestamps);

    if (isFinite(actualXMin) && isFinite(actualXMax)) {
      const idealTimestamps = generateTickValues(actualXMin, actualXMax);
      const uniqueIdealTimestamps = [...new Set(idealTimestamps)];

      finalTickValues = uniqueIdealTimestamps
        .map((idealTs) => {
          let closestDate = dateScaleDomain[0];
          let minDiff = Math.abs(idealTs - domainTimestamps[0]);

          for (let i = 0; i < dateScaleDomain.length; i++) {
            const diff = Math.abs(idealTs - domainTimestamps[i]);
            if (diff < minDiff) {
              minDiff = diff;
              closestDate = dateScaleDomain[i];
            }
          }
          return closestDate;
        })
        .filter((value, index, self) => self.indexOf(value) === index);
    }
  }

  const getTotalValue = () => {
    const total = totalValue?.find(
      (t: any) => t.date === selectedDate
    )?.totalValue;
    return total ? total + unit : undefined;
  };
  return (
    <div
      ref={chartRef}
      className={
        isWeb
          ? styles.Stackbar__Wrapper__Web
          : styles.Stackbar__Wrapper
      }
    >
      <div className={styles.Stackbar__Header}>
        <div className={styles.GraphHeader__TitleContainer}>
          {selectedDate && (
            <div
              className="heading4"
              style={{
                color: ThemeStore.colors.text_primary,
                marginRight: '.5em',
              }}
            >
              {formatDate(selectedDate, selectedTimeSet)}
            </div>
          )}
          <div
            className={`${styles.GraphHeader__Title} ${
              selectedDate ? 'heading4L' : 'heading4'
            }`}
            style={{ color: ThemeStore.colors.text_primary }}
          >
            {t(title) + ' '}
            {selectedBarData && getTotalValue()}
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {legendItems.map((item) => {
            if (
              item.type === 'single' &&
              item.config &&
              item.label &&
              item.colorKey
            ) {
              const gk = item.config;
              return (
                <button
                  key={item.id}
                  onClick={() => toggleKeyVisibility(gk.key)}
                  className={`detail ${styles.Stackbar__Key}`}
                  style={{
                    padding: '0',
                    border: 'none',
                    borderBottom: `4px solid ${
                      visibleKeys[gk.key] ? colorScale(gk.key) : axisLineColor
                    }`,
                    backgroundColor: 'transparent',
                    color: ThemeStore.colors.text_secondary || '#505558',
                    borderRadius: '1px',
                    opacity:
                      selectedDate &&
                      !visibleKeys[gk.key] &&
                      Object.values(visibleKeys).some((v) => !v)
                        ? 0.5
                        : 1,
                    cursor: isNullData || isInherentlyZeroData ? 'default' : 'pointer',
                  }}
                >
                  {item.label}
                  {selectedBarData &&
                    visibleKeys[gk.key] &&
                    selectedBarData[`widgets.${subtype}.${gk.key}`] != null && (
                      <strong style={{ marginLeft: '0.25em', fontWeight: 600 }}>
                        | {formatToMaxFourDigits(selectedBarData[`widgets.${subtype}.${gk.key}`])}{' '}
                        {unit}
                      </strong>
                    )}
                </button>
              );
            } else if (
              item.type === 'group' &&
              item.memberConfigs &&
              item.memberConfigs.length > 0
            ) {
              const groupMemberKeys = item.memberConfigs.map((mc) => mc.key);
              const anyCombinedVisible = groupMemberKeys.some(
                (mk) => visibleKeys[mk]
              );

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    // Disable toggling only when there is no data (null) or all original values are inherently zero.
                    if (isInherentlyZeroData || isNullData) return;
                    
                    const newVisibilityState = !anyCombinedVisible;
                    if (!newVisibilityState) {
                      const visibleKeysOutsideThisGroup = seriesKeys.filter(
                        (k) => visibleKeys[k] && !groupMemberKeys.includes(k)
                      ).length;
                      const visibleKeysInThisGroup = groupMemberKeys.filter(
                        (k) => visibleKeys[k]
                      ).length;

                      if (
                        visibleKeysOutsideThisGroup === 0 &&
                        visibleKeysInThisGroup > 0 &&
                        groupMemberKeys.every((mk) => visibleKeys[mk])
                      ) {
                        const totalVisibleKeys = seriesKeys.filter(
                          (k) => visibleKeys[k]
                        ).length;
                        if (totalVisibleKeys === groupMemberKeys.length) {
                          return;
                        }
                      }
                    }

                    setVisibleKeys((prev) => {
                      const updated = { ...prev };
                      const effectiveNewState = newVisibilityState;

                      if (!newVisibilityState) {
                        const otherVisibleKeys = seriesKeys.filter(
                          (k) => !groupMemberKeys.includes(k) && prev[k]
                        ).length;
                        const wouldBeVisibleAfterToggle = { ...prev };
                        groupMemberKeys.forEach((mk) => {
                          wouldBeVisibleAfterToggle[mk] = false;
                        });
                        const numVisibleAfterToggle = seriesKeys.filter(
                          (k) => wouldBeVisibleAfterToggle[k]
                        ).length;

                        if (
                          numVisibleAfterToggle === 0 &&
                          otherVisibleKeys === 0
                        ) {
                          const totalCurrentlyVisible = seriesKeys.filter(
                            (k) => prev[k]
                          ).length;
                          const groupCurrentlyFullyVisible =
                            groupMemberKeys.every((mk) => prev[mk]);
                          if (
                            totalCurrentlyVisible === groupMemberKeys.length &&
                            groupCurrentlyFullyVisible
                          ) {
                            return prev;
                          }
                        }
                      }

                      groupMemberKeys.forEach((mk) => {
                        updated[mk] = effectiveNewState;
                      });
                      return updated;
                    });
                  }}
                  className={`detail ${styles.Stackbar__Key}`}
                  style={{
                    display: 'inline-flex',
                    backgroundColor: 'transparent',
                    color: ThemeStore.colors.text_secondary || '#505558',
                    borderRadius: '1px',
                    opacity:
                      selectedDate &&
                      !anyCombinedVisible &&
                      Object.values(visibleKeys).some((v) => !v)
                        ? 0.5
                        : 1,
                    cursor: isNullData || isInherentlyZeroData ? 'default' : 'pointer',
                    marginRight: '10px',
                  }}
                >
                  {(() => {
                    const contributingVisibleMembers =
                      selectedBarData && anyCombinedVisible
                        ? item.memberConfigs.filter(
                            (m) =>
                              visibleKeys[m.key] &&
                              selectedBarData[`widgets.${subtype}.${m.key}`] !=
                                null &&
                              selectedBarData[`widgets.${subtype}.${m.key}`] > 0
                          )
                        : [];
                    const numActiveContributors =
                      contributingVisibleMembers.length;

                    return (
                      <>
                        {item.memberConfigs.map((mc, idx, arr) => (
                          <React.Fragment key={mc.key}>
                            <span
                              style={{
                                padding: '0 0.25em',
                                borderBottom: `4px solid ${
                                  anyCombinedVisible
                                    ? colorScale(mc.key)
                                    : axisLineColor
                                }`,
                              }}
                            >
                              {getGraphKeyLabel(mc.key)}
                            </span>
                            {selectedBarData &&
                              anyCombinedVisible &&
                              selectedBarData[`widgets.${subtype}.${mc.key}`] !=
                                null && (
                                <span
                                  style={{
                                    fontWeight: 600,
                                    borderBottom: `4px solid ${
                                      anyCombinedVisible
                                        ? numActiveContributors > 1 &&
                                          mc.key === arr[arr.length - 1].key
                                          ? colorScale(arr[arr.length - 1].key)
                                          : colorScale(mc.key)
                                        : 'transparent'
                                    }`,
                                  }}
                                >
                                  |{' '}
                                  {
                                    formatToMaxFourDigits(selectedBarData[
                                      `widgets.${subtype}.${mc.key}`
                                    ])
                                  } {' '}
                                  {unit}
                                </span>
                              )}
                          </React.Fragment>
                        ))}
                      </>
                    );
                  })()}
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
      <svg
        ref={svgRef}
        width={chartWidth - platformConfig.containerPadding}
        height={height - platformConfig.headerHeight}
        onMouseMove={handlePointerMove}
        onMouseLeave={handlePointerLeave}
        onTouchMove={handlePointerMove}
        onTouchEnd={cleanupTouchInteraction}
        onTouchCancel={cleanupTouchInteraction}
        onTouchStart={() => {
          if (isNullData || is0ValueData) return;
          triggerThrottledHaptic();
        }}
        style={{
          userSelect: 'none',
          cursor: isNullData || is0ValueData ? 'default' : 'pointer',
        }}
      >
        <AxisLeft
          left={margin.left}
          scale={yScale}
          numTicks={is0ValueData || isNullData ? 0 : 4}
          stroke={axisLineColor}
          hideTicks={true}
          tickFormat={(value) =>
            value.toLocaleString(browserLanguage, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })
          }
          tickLabelProps={() => ({
            fill: axisTickLabelColor,
            fontSize: 12,
            textAnchor: 'end',
            dy: '0.33em',
            opacity: is0ValueData || isNullData ? 0 : 1,
          })}
          top={margin.top + CHART_CONFIG.axis.leftTopOffset}
          labelProps={{
            fill: axisTickLabelColor,
            fontSize: 12,
            textAnchor: 'middle',
            x: -10,
            y: -10,
          }}
          label={unit}
          strokeWidth={1}
          tickClassName={`${styles.BarStack__Axis} detail`}
          labelClassName={`${styles['rotate']} ${styles['rotate__360']} detail`}
        />
        <AxisBottom
          top={yMax + margin.bottom + CHART_CONFIG.axis.bottomTopOffset}
          left={margin.left}
          scale={dateScale}
          numTicks={4}
          tickValues={finalTickValues}
          stroke={axisLineColor}
          hideTicks={true}
          tickLabelProps={(value, index, ticks) => {
            const isLastTick = index === ticks.length - 1;
            return {
              fill: axisTickLabelColor,
              fontSize: 12,
              textAnchor: isLastTick ? 'end' : 'middle',
              dx: isLastTick ? -5 : 0,
            };
          }}
          tickFormat={(value) => {
            const date = DateTime.fromJSDate(new Date(value as any));
            return formatDate(date.toISO() as string, selectedTimeSet);
          }}
          axisClassName={`${styles.BarStack__Axis} detail`}
        />
        {(chartWidth > 0 && isNullData) || (chartWidth > 0 && is0ValueData) ? (
          // Did some reaerch and foreginObject should not bring issues since it has browser and mobile support with ionic.
          <foreignObject
            x={0}
            y={0}
            width={chartWidth - platformConfig.containerPadding}
            height={height - platformConfig.headerHeight}
            style={{
              pointerEvents: 'none', // so the axes remain interactive if needed
            }}
          >
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.9,
              }}
              className={styles.Stackbar__NoData}
            >
              <IconWrapper
                iconName={is0ValueData ? 'ellipse' : 'no_data_chart'}
                style={{ marginBottom: 8}}
                className={styles.Stackbar__NoData__Icon}
              />
              <div className={`${styles.Stackbar__NoData__Label} detail`} style={{ width: '70%' }}>
                {is0ValueData
                  ? t('charts.everything_is_zero')
                  : t('charts.data_unavailable')}
              </div>
            </div>
          </foreignObject>
        ) : (
          <Group top={margin.top + CHART_CONFIG.chartPadding.groupOffset} left={margin.left}>
            <BarStack<any, string>
              data={processedData}
              keys={seriesKeys}
              x={getDate}
              xScale={dateScale}
              yScale={yScale}
              color={colorScale}
            >
              {(barStacks) =>
                barStacks.map((barStack) =>
                  barStack.bars.map((bar, barIndexInStack) => {
                    const barDataForDate = bar.bar.data;
                    const currentSeriesKey = bar.key;
                    const currentKeyConfig =
                      seriesKeyConfigs.get(currentSeriesKey);
                    const barDate = getDate(barDataForDate);

                    if (!currentKeyConfig || !visibleKeys[currentSeriesKey])
                      return null;

                    // Don't render bars with no data to avoid stroke artifacts
                    const dataValue = barDataForDate[currentSeriesKey];
                    if (!dataValue || dataValue <= 0 || bar.height <= 0)
                      return null;

                    const isSelectedDate = selectedDate === barDate;
                    const barFillColor =
                      selectedDate && !isSelectedDate
                        ? getGreyScaledColor(bar.color)
                        : bar.color;

                    let radiusTopLeft = false,
                      radiusTopRight = false,
                      radiusBottomLeft = false,
                      radiusBottomRight = false;
                    const radiusValue = 4;

                    if (!currentKeyConfig.combined) {
                      radiusTopLeft = true;
                      radiusTopRight = true;
                      radiusBottomLeft = true;
                      radiusBottomRight = true;
                    } else {
                      const currentKeyIndexInSeries =
                        seriesKeys.indexOf(currentSeriesKey);
                      let isTopMostVisibleCombined = true;
                      for (
                        let i = currentKeyIndexInSeries + 1;
                        i < seriesKeys.length;
                        i++
                      ) {
                        const nextSeriesKey = seriesKeys[i];
                        const nextKeyConfig =
                          seriesKeyConfigs.get(nextSeriesKey);
                        if (
                          nextKeyConfig &&
                          nextKeyConfig.combined &&
                          visibleKeys[nextSeriesKey] &&
                          barDataForDate[nextSeriesKey] > 0
                        ) {
                          isTopMostVisibleCombined = false;
                          break;
                        }
                        if (nextKeyConfig && !nextKeyConfig.combined) {
                          break;
                        }
                      }
                      if (isTopMostVisibleCombined) {
                        radiusTopLeft = true;
                        radiusTopRight = true;
                      }

                      let isBottomMostVisibleCombined = true;
                      for (let i = currentKeyIndexInSeries - 1; i >= 0; i--) {
                        const prevSeriesKey = seriesKeys[i];
                        const prevKeyConfig =
                          seriesKeyConfigs.get(prevSeriesKey);
                        if (
                          prevKeyConfig &&
                          prevKeyConfig.combined &&
                          visibleKeys[prevSeriesKey] &&
                          barDataForDate[prevSeriesKey] > 0
                        ) {
                          isBottomMostVisibleCombined = false;
                          break;
                        }
                        if (prevKeyConfig && !prevKeyConfig.combined) {
                          break;
                        }
                      }
                      if (isBottomMostVisibleCombined) {
                        radiusBottomLeft = true;
                        radiusBottomRight = true;
                      }
                    }

                    const actualRadius =
                      radiusTopLeft ||
                      radiusTopRight ||
                      radiusBottomLeft ||
                      radiusBottomRight
                        ? radiusValue
                        : 0;

                    // const initialYPos = bar.y + bar.height;
                    // const initialHeightVal = 0;
                    // const targetYPos = bar.y;
                    // const targetHeightVal = Math.max(0, bar.height);
                        
                    // TEST FOR ANIMATION TRIALS 
                    const initialYPos = yMax;
                    const initialHeightVal = 0;
                    const targetYPos = bar.y;
                    const targetHeightVal = Math.max(0, bar.height);

                    const barXToPass = currentKeyConfig.combined
                      ? bar.x + 0.5
                      : bar.x;
                    const barWidthToPass = currentKeyConfig.combined
                      ? Math.max(0, bar.width - 1)
                      : bar.width;

                    return (
                      <AnimatedBar
                        key={`animated-bar-${currentSeriesKey}-${bar.index}-${barDate}`}
                        xPos={barXToPass}
                        initialY={initialYPos}
                        initialHeight={initialHeightVal}
                        targetY={targetYPos}
                        targetHeight={targetHeightVal}
                        width={barWidthToPass}
                        fill={barFillColor}
                        stroke={
                          currentKeyConfig.combined ? barFillColor : 'white'
                        }
                        strokeWidth={currentKeyConfig.combined ? 0 : 1}
                        radius={actualRadius}
                        radiusTopLeft={radiusTopLeft}
                        radiusTopRight={radiusTopRight}
                        radiusBottomLeft={radiusBottomLeft}
                        radiusBottomRight={radiusBottomRight}
                        shouldAnimate={animate}
                        delay={barIndexInStack * 30}
                        springConfig={{ tension: 170, friction: 26 }}
                        isCombined={currentKeyConfig.combined}
                        barXForCombined={
                          currentKeyConfig.combined ? bar.x : undefined
                        }
                        barWidthForCombined={
                          currentKeyConfig.combined ? bar.width : undefined
                        }
                      />
                    );
                  })
                )
              }
            </BarStack>
          </Group>
        )}
      </svg>
    </div>
  );
};

export default Stackbar;
